import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import commonSlice, { commonActions } from 'app/store/common.slice';
import BreadCrumbs from 'components/layouts/BreadCrumbs';
import PageIntro from 'components/PageIntro/PageIntro';
import UIBox from 'components/ui/UIBox';
import UIButton from 'components/ui/UIButton';
import UIContainer from 'components/ui/UIContainer';
import UIFlex from 'components/ui/UIFlex';
import UIText from 'components/ui/UIText';
import WithLoader from 'components/WithLoader';
import SubCategoryRow from 'components/SubCategoryRow';
import SubCategoryProgressCard from 'components/SubCategoryProgressCard';
import { CEFR_META, DEFAULT_ACCENT, hexToRgba } from 'components/cefrMeta';
import { LearningSession, SubCategoryTO, WordSetCategoryType } from 'app/types';
import { fillUrlIds, pagePaths } from 'routes/paths';
import WordsTable, { RowsSelected } from 'components/WordsTable';
import { axiosInstance } from 'setup/axiosInstance';
import {
  Close as CloseIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { NotificationCenterContext } from 'components/NotificationCenter';
import TableSettingsDialog from 'components/TableSettingsDialog';
import {
  createDefaultColumnSettings,
  tableSettingsToColumnsVisibility,
} from 'components/tableSettings';
import { useTableSettings } from 'hooks/useTableSettings';

interface ICategoryOverviewProps {}

type VocabularyWordStatus = 'learned' | 'notLearned' | 'repeat';

const statusOptions: {
  label: string;
  value: VocabularyWordStatus;
  isActiveSpeech: boolean;
  isLearned: boolean;
}[] = [
  {
    label: 'Не вивчено',
    value: 'notLearned',
    isLearned: false,
    isActiveSpeech: false,
  },
  {
    label: 'Вивчено',
    value: 'learned',
    isLearned: true,
    isActiveSpeech: false,
  },
  {
    label: 'Потребує повторення',
    value: 'repeat',
    isLearned: true,
    isActiveSpeech: false,
  },
];
const DEFAULT_DICTIONARY_IRREGULAR_VERBS_COLUMNS = createDefaultColumnSettings({
  number: true,
  infinitive: true,
  infinitiveTranscription: true,
  pastSimple: true,
  pastSimpleTranscription: true,
  pastParticiple: true,
  pastParticipleTranscription: true,
  translation: true,
});

const CategoryOverview: React.FC<ICategoryOverviewProps> = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { categoryName = '', wordSetType = '' } = useParams();
  const { pushNotification } = useContext(NotificationCenterContext);
  const isIrregularVerbPage = wordSetType === 'irregular-verb';
  const [adding, setAdding] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [pendingRowsSelected, setPendingRowsSelected] = useState<RowsSelected>(
    {},
  );
  const [selectedStatus, setSelectedStatus] =
    useState<VocabularyWordStatus>('notLearned');
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const [learningStartLoading, setLearningStartLoading] = useState(false);
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(
    null,
  );
  const {
    resetSettings: resetTableSettings,
    saveSettings: saveTableSettings,
    settings: tableSettings,
  } = useTableSettings(
    'DICTIONARY_IRREGULAR_VERBS',
    DEFAULT_DICTIONARY_IRREGULAR_VERBS_COLUMNS,
  );
  const irregularVerbColumns = React.useMemo(
    () => tableSettingsToColumnsVisibility(tableSettings, { select: true }),
    [tableSettings],
  );

  const selectedVerbsCount =
    Object.values(pendingRowsSelected).filter(Boolean).length;

  const { loading: wordSetCategoryLoading } = useAppSelector((store) =>
    commonSlice
      .getSelectors()
      .getWordSetCategory(store.common, wordSetType as WordSetCategoryType),
  );

  const categoryData = useAppSelector((store) =>
    commonSlice
      .getSelectors()
      .getCategoryData(
        store.common,
        wordSetType as WordSetCategoryType,
        categoryName,
      ),
  );
  const subCategoryData = useAppSelector((store) =>
    commonSlice.getSelectors().getSubcategoryData(store.common, categoryName),
  );
  const irregularVerbWords = useAppSelector(
    (store) => store.common.irregularVerbWordsByLevel[categoryName],
  );

  useEffect(() => {
    if (!categoryName) {
      return;
    }

    if (isIrregularVerbPage) {
      dispatch(
        commonActions.getIrregularVerbWords({ levelBased: categoryName }),
      );
    } else {
      dispatch(commonActions.getCategories({ wordSetType, categoryName }));
    }
  }, [categoryName, dispatch, isIrregularVerbPage, wordSetType]);

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  const openAddDialog = (rowsSelected: RowsSelected) => {
    setPendingRowsSelected(rowsSelected);
    setSelectedStatus('notLearned');
    setAddDialogOpen(true);
  };

  const closeAddDialog = () => {
    setAddDialogOpen(false);
  };

  const addIrregularVerbs = async () => {
    const selectedOption = statusOptions.find(
      ({ value }) => value === selectedStatus,
    );

    if (!selectedOption || !selectedVerbsCount) {
      return;
    }

    try {
      setAdding(true);
      await axiosInstance.post(
        `/vocabulary/verbs/`,
        (irregularVerbWords?.data || [])
          .filter(({ id }) => !!pendingRowsSelected[id])
          .map((record) => ({
            ...record,
            isLearned: selectedOption.isLearned,
            isActiveSpeech: selectedOption.isActiveSpeech,
          })),
      );
      setAddDialogOpen(false);
      setPendingRowsSelected({});
      setSelectionResetKey((prev) => prev + 1);
      dispatch(
        commonActions.getIrregularVerbWords({ levelBased: categoryName }),
      );
      pushNotification(
        `Додано ${selectedVerbsCount} слів в словник`,
        'success',
        undefined,
        'center',
      );
    } catch (e) {
      console.error(e);
    } finally {
      setAdding(false);
    }
  };

  const startLearning = async (rowsSelected: RowsSelected) => {
    const irregularVerbIds = Object.entries(rowsSelected)
      .filter(([, selected]) => selected)
      .map(([id]) => Number(id));

    if (!irregularVerbIds.length) {
      return;
    }

    setLearningStartLoading(true);
    try {
      const { data: learningSession } =
        await axiosInstance.post<LearningSession>('/learning-sessions', {
          mode: 'MANUAL',
          source: 'DICTIONARY',
          irregularVerbIds,
        });
      navigate(
        fillUrlIds(pagePaths.learningSession, {
          learningSessionId: String(learningSession.id),
        }),
      );
    } finally {
      setLearningStartLoading(false);
    }
  };

  const cefr = CEFR_META[categoryName];
  const accent = cefr?.color ?? DEFAULT_ACCENT;
  const eyebrowText = (() => {
    switch (wordSetType) {
      case 'level-based':
        return 'Підкатегорія · рівень CEFR';
      case 'topic-related':
        return 'Підкатегорія · тематична лексика';
      case 'most-common':
        return 'Підкатегорія · найвживаніше';
      case 'collocations':
        return 'Підкатегорія · словосполучення';
      case 'phrasal-verbs':
        return 'Підкатегорія · фразові дієслова';
      case 'idioms':
        return 'Підкатегорія · ідіоми';
      case 'irregular-verb':
        return 'Підкатегорія · неправильні дієслова';
      default:
        return 'Підкатегорія';
    }
  })();

  const subCategoryItems: SubCategoryTO[] = subCategoryData?.data ?? [];
  const agg = subCategoryItems.reduce(
    (acc, s) => ({
      learned: acc.learned + (s.learnedWords || 0),
      added: acc.added + (s.addedWords || 0),
      total: acc.total + (s.totalWords || 0),
    }),
    { learned: 0, added: 0, total: 0 },
  );

  const resumeSubcategoryName = (() => {
    let best: string | undefined;
    let bestScore = -1;
    subCategoryItems.forEach((s) => {
      if (s.addedWords <= 0 || s.totalWords <= 0) return;
      if (s.learnedWords >= s.totalWords) return;
      const score =
        (s.learnedWords / s.totalWords) * 1000 +
        s.addedWords / s.totalWords;
      if (score > bestScore) {
        bestScore = score;
        best = s.subcategoryName;
      }
    });
    return best;
  })();

  const resumeLink = resumeSubcategoryName
    ? fillUrlIds(pagePaths.subCategoryOverview, {
        wordSetType: wordSetType as WordSetCategoryType,
        categoryName,
        subcategoryName: resumeSubcategoryName,
      })
    : undefined;

  return (
    <>
      <UIContainer>
        <BreadCrumbs />
      </UIContainer>

      <WithLoader loading={wordSetCategoryLoading}>
        {!categoryName || !categoryData ? null : (
          <>
            {!isIrregularVerbPage && (
              <UIContainer sx={{ pb: { xs: 2, md: 3.5 } }}>
                <PageIntro
                  title={categoryData.title}
                  description={categoryData.subTitle}
                  eyebrow={
                    <UIFlex
                      ai={'center'}
                      jc={'flex-start'}
                      sx={{ flexWrap: 'nowrap', gap: 1.5 }}
                    >
                      <UIBox
                        sx={{
                          alignItems: 'center',
                          bgcolor: hexToRgba(accent, 0.12),
                          border: '1px solid',
                          borderColor: hexToRgba(accent, 0.22),
                          borderRadius: 1.25,
                          color: accent,
                          display: 'inline-flex',
                          flex: 'none',
                          fontSize: 18,
                          fontWeight: 800,
                          height: 46,
                          justifyContent: 'center',
                          overflow: 'hidden',
                          width: 46,
                          '& svg': { width: 32, height: 'auto' },
                        }}
                      >
                        {cefr ? (
                          <cefr.Icon />
                        ) : (
                          categoryName.slice(0, 2).toUpperCase()
                        )}
                      </UIBox>
                      <UIText
                        sx={{
                          color: 'primary.subtitle',
                          fontSize: 13,
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {eyebrowText}
                      </UIText>
                    </UIFlex>
                  }
                  aside={
                    <SubCategoryProgressCard
                      learned={agg.learned}
                      added={agg.added}
                      total={agg.total || categoryData.totalWords}
                      totalTopics={
                        subCategoryItems.length ||
                        categoryData.subCategoryCount
                      }
                      accent={accent}
                      resumeLink={resumeLink}
                    />
                  }
                />
              </UIContainer>
            )}

            <UIContainer sx={{ mb: 10 }}>
              {isIrregularVerbPage ? (
                <WithLoader loading={irregularVerbWords?.loading ?? true}>
                  <WordsTable
                    renderToolbar={({ rowsSelected }) => {
                      const selectedCount =
                        Object.values(rowsSelected).filter(Boolean).length;

                      return (
                        <UIFlex
                          justifyContent={'space-between'}
                          mt={2}
                          minHeight={50}
                          spacing={2}
                        >
                          <UIFlex spacing={1}>
                            <Button
                              disabled={!selectedCount || learningStartLoading}
                              onClick={() => startLearning(rowsSelected)}
                              variant={'contained'}
                            >
                              {'Почати вправи на вивчення'}
                            </Button>
                          </UIFlex>

                          <UIFlex spacing={1}>
                            <Button
                              loading={adding}
                              onClick={() => openAddDialog(rowsSelected)}
                              variant={'contained'}
                              disabled={!selectedCount}
                            >
                              {'+ Додати в Мій Словник'}
                            </Button>
                            <UIButton
                              onClick={(e) =>
                                setSettingsAnchor(e.currentTarget)
                              }
                            >
                              <SettingsIcon />
                            </UIButton>
                          </UIFlex>
                        </UIFlex>
                      );
                    }}
                    words={irregularVerbWords?.data || []}
                    columns={irregularVerbColumns}
                    selectionResetKey={selectionResetKey}
                    showDescriptionInInfinitive
                    tableSettings={tableSettings}
                  />
                </WithLoader>
              ) : (
                <WithLoader loading={!!subCategoryData?.loading}>
                  <>
                    {!!subCategoryItems.length && (
                      <UIFlex
                        ai={'center'}
                        sx={{ flexWrap: 'nowrap', gap: 1.5, mb: 2 }}
                      >
                        <UIText
                          sx={{
                            color: 'primary.subtitle',
                            fontSize: 12,
                            fontWeight: 800,
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {`${subCategoryItems.length} тем · ${
                            agg.total || categoryData.totalWords
                          } слів`}
                        </UIText>
                        <UIBox
                          sx={{ bgcolor: 'divider', flex: 1, height: '1px' }}
                        />
                      </UIFlex>
                    )}
                    <Stack spacing={1}>
                      {subCategoryItems.map((sub, index) => (
                        <SubCategoryRow
                          key={sub.subcategoryName}
                          index={index}
                          title={sub.subcategoryName}
                          totalWords={sub.totalWords}
                          addedWords={sub.addedWords}
                          learnedWords={sub.learnedWords}
                          color={accent}
                          link={fillUrlIds(pagePaths.subCategoryOverview, {
                            wordSetType: wordSetType as WordSetCategoryType,
                            categoryName,
                            subcategoryName: sub.subcategoryName,
                          })}
                        />
                      ))}
                    </Stack>
                  </>
                </WithLoader>
              )}
            </UIContainer>
          </>
        )}
      </WithLoader>

      <Dialog
        disableScrollLock
        open={addDialogOpen}
        onClose={closeAddDialog}
        fullWidth
        maxWidth={'xs'}
      >
        <DialogTitle
          sx={{
            alignItems: 'flex-start',
            display: 'flex',
            gap: 2,
            justifyContent: 'space-between',
          }}
        >
          {`Виберіть стан для ${selectedVerbsCount} обраних слів:`}
          <IconButton onClick={closeAddDialog} size={'small'}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {statusOptions.map(({ label, value }) => (
            <Box
              key={value}
              onClick={() => setSelectedStatus(value)}
              sx={{
                alignItems: 'center',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                py: 1,
              }}
            >
              {label}
              <Checkbox
                checked={selectedStatus === value}
                disableTouchRipple
                onChange={() => setSelectedStatus(value)}
              />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddDialog}>{'Скасувати'}</Button>
          <Button
            loading={adding}
            onClick={addIrregularVerbs}
            variant={'contained'}
          >
            {'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>
      <TableSettingsDialog
        anchorEl={settingsAnchor}
        settings={tableSettings}
        onClose={() => setSettingsAnchor(null)}
        onReset={resetTableSettings}
        onSave={saveTableSettings}
      />
    </>
  );
};

export default CategoryOverview;
